import { supabase } from '../config/SupabaseClient';

/**
 * Utilidades para manejo de llamadas a la API
 */

/**
 * Limpia los datos de autenticación (logout)
 * @param {Function} redirectCallback - Callback opcional para redirigir después del logout
 */
export const clearAuthData = async (redirectCallback = null) => {
  try {
    // Logout de Supabase
    await supabase.auth.signOut();
    
    // Limpiar cualquier dato local adicional
    localStorage.removeItem('user');
    localStorage.removeItem('email');
    localStorage.removeItem('role_id');
    localStorage.clear(); // Borra cualquier otro dato almacenado localmente
    
    if (redirectCallback) {
      redirectCallback();
    } else {
      // Por defecto, redirigir al inicio de sesión
      window.location.href = '/signin';
    }
  } catch (error) {
    console.error('Error en logout:', error);
    if (redirectCallback) {
      redirectCallback();
    } else {
      window.location.href = '/';
    }
  }
};

/**
 * Ejecuta múltiples llamadas a la API en paralelo de forma segura
 * @param {Array<Function>} apiCalls - Array de funciones que retornan promesas
 * @param {Function} isMountedCheck - Función para verificar si el componente está montado
 * @returns {Promise<Array>} - Array con los resultados
 */
export const executeParallelApiCalls = async (apiCalls, isMountedCheck = () => true) => {
  try {
    const results = await Promise.all(apiCalls);
    return isMountedCheck() ? results : [];
  } catch (error) {
    console.error('Error in parallel API calls:', error);
    throw error;
  }
};

/**
 * Ejecuta llamadas a la API en secuencia
 * @param {Array<Function>} apiCalls - Array de funciones que retornan promesas
 * @param {Function} isMountedCheck - Función para verificar si el componente está montado
 * @returns {Promise<Array>} - Array con los resultados
 */
export const executeSequentialApiCalls = async (apiCalls, isMountedCheck = () => true) => {
  const results = [];
  
  for (const apiCall of apiCalls) {
    if (!isMountedCheck()) break;
    
    try {
      const result = await apiCall();
      results.push(result);
    } catch (error) {
      console.error('Error in sequential API call:', error);
      throw error;
    }
  }
  
  return results;
};

/**
 * Wrapper para ejecutar una función API con manejo de errores
 * @param {Function} apiFunction - Función de API
 * @param {Function} onSuccess - Callback de éxito
 * @param {Function} onError - Callback de error
 * @returns {Promise}
 */
export const safeApiCall = async (apiFunction, onSuccess = null, onError = null) => {
  try {
    const result = await apiFunction();
    if (onSuccess) onSuccess(result);
    return result;
  } catch (error) {
    if (onError) onError(error);
    else console.error('API call error:', error);
    throw error;
  }
};

/**
 * Crea un manejador de estado para actualizaciones condicionales
 * @param {Function} setState - Función setState de React
 * @param {Function} isMountedCheck - Función para verificar si está montado
 * @returns {Function} - Función setState segura
 */
export const createSafeStateSetter = (setState, isMountedCheck) => {
  return (value) => {
    if (isMountedCheck()) {
      setState(value);
    }
  };
};

/**
 * Batch de actualizaciones de estado para múltiples resultados de API
 * @param {Array<{setter: Function, value: any}>} updates - Array de actualizaciones
 * @param {Function} isMountedCheck - Función para verificar si está montado
 */
export const batchStateUpdates = (updates, isMountedCheck = () => true) => {
  if (!isMountedCheck()) return;
  
  updates.forEach(({ setter, value }) => {
    setter(value);
  });
};
